import * as React from 'react';
import styled from 'styled-components';
import { Icon } from '../../icon';
import { useChooseRef, useKeyStrokeListener } from '../../../utils';

interface IModeIndicatorProps {
    className?: string;
    hidden?: boolean;
    isInCompactMode?: boolean;
    onClick?(): void;
}

const ModeIndicatorBase = React.memo((props: IModeIndicatorProps) => {
    const { className, hidden, onClick } = props;

    const rootElementRef = useChooseRef(React.createRef()) as React.RefObject<HTMLDivElement>;
    const {subscribe, unsubscribe} = useKeyStrokeListener(rootElementRef);

    React.useEffect(() => {
        if(onClick !== undefined) {
            subscribe(onClick, ' ', 'Enter');
        }
        return unsubscribe;
    }, [subscribe, unsubscribe, onClick]);

    return hidden ? null : (
        <div className={className} ref={rootElementRef} onClick={onClick} tabIndex={0}>
            <Icon className="ap-treeview-expandIcon" iconName="ap-right-arrow-thin2.outline" iconSize="16" />
        </div>
    );

});

ModeIndicatorBase.displayName = 'ModeIndicatorBase';

export const ModeIndicator = styled(ModeIndicatorBase).attrs({ className: 'ap-treeview-modeIndicator' })`
    position: absolute;
    z-index: 1;
    width: 28px;
    height: 28px;
    cursor: pointer;
    top: 50%;
    left: 100%;
    border-style: solid;
    transform: translate(-50%, -50%);
    border-radius: ${({ theme: { treeView: { composites } } }) => composites.borderRadius.modeIndicator};
    border-width: ${({ theme: { treeView: { composites } } }) => composites.borderWidth.modeIndicator};
    border-color: ${({ theme: { treeView: { colors } } }) => colors.modeIndicator.borderColor};
    background-color: ${({ theme: { treeView: { colors } } }) => colors.modeIndicator.backgroundColor};
    box-shadow: ${({ theme: { treeView: { composites } } }) => composites.modeIndicatorBoxShadow};

    ${Icon} {
        position: absolute;
        top: 50%;
        left: 50%;
        transition: all 0.15s ease-in-out;
        transform-origin: center;
        transform: translate(-50%, -50%) ${({ isInCompactMode }) => !isInCompactMode && 'rotate(180deg)'};
        fill: ${({ theme: { treeView: { colors } } }) => colors.modeIndicator.iconColor};
    }

    :focus {
        outline: inherit;
        box-shadow: 0px 0px 4px ${({ theme: { semantics: { palette } } }) => palette.themePrimary};
    }
`;

ModeIndicator.displayName = 'ModeIndicator';
